<template>
  <div>
    <div class="font-bold mb-3">
      {{ t("account.settings.contract.title") }}
    </div>

    <v-btn
      v-if="
        account.contractType === 'custom' ||
        account.contractEnvelope?.deliverable.status === 'generated'
      "
      target="_blank"
      variant="outlined"
      color="primary"
      :disabled="loading"
      append-icon="mdi-open-in-new"
      @click="$emit('downloadContract')"
      >{{ t("account.settings.contract.viewSignedContract") }}
    </v-btn>
    <v-alert
      v-else
      class="mt-3"
      type="info"
      color="primary"
      variant="tonal"
      density="compact"
      border
    >
      {{
        account.contractType === "signatureApi"
          ? t("account.settings.contract.notSigned")
          : t("account.settings.contract.noContract")
      }}
    </v-alert>

    <AdminOnlyCard class="mt-3">
      <template v-if="account.contractType !== 'custom'">
        <v-switch
          :model-value="contractType === 'signatureApi'"
          color="primary"
          :label="t('account.settings.contract.requireContractSignature')"
          hide-details="auto"
          :disabled="loading || !contractInfoValid"
          @update:model-value="$emit('update:contractType', $event ? 'signatureApi' : null)"
        ></v-switch>

        <v-alert
          v-if="!contractInfoValid"
          class="mt-2"
          type="warning"
          color="warning"
          variant="tonal"
          density="compact"
          border
        >
          {{ t("account.settings.contract.missingContractInfo") }}
          <br />
          <v-btn variant="outlined" :to="{ name: 'SupervisorOrgAdmin' }">
            {{ t("account.settings.contract.goToSupervisorOrgContractSettings") }}
          </v-btn>
        </v-alert>

        <v-select
          v-if="contractType === 'signatureApi'"
          :model-value="contractTemplateId"
          class="mt-3"
          :items="contractTemplateItems"
          :label="t('account.settings.contract.contractTemplate')"
          :rules="[required]"
          :disabled="!!account.contractEnvelope || loading"
          @update:model-value="$emit('update:contractTemplateId', $event)"
        />

        <div v-if="contractType === 'signatureApi' && account.contractEnvelope">
          <div>
            {{ t("account.settings.contract.operatorStatus") }}
            {{ signatureAPIStatuses.operator }}
          </div>
          <div>
            {{ t("account.settings.contract.supervisorOrgStatus") }}
            {{ signatureAPIStatuses.supervisorOrg }}
          </div>
          <v-btn
            :href="`https://dashboard.signatureapi.com/envelopes/${account.contractEnvelope.id}`"
            target="_blank"
            variant="outlined"
            append-icon="mdi-open-in-new"
            class="mt-3 mb-3"
            >{{ t("account.settings.contract.openSignatureAPI") }}
          </v-btn>
        </div>
      </template>

      <v-file-input
        :model-value="contractCustomFile"
        class="mt-3"
        :label="t('account.settings.contract.uploadContractHint')"
        persistent-hint
        variant="outlined"
        prepend-inner-icon="mdi-paperclip"
        accept="application/pdf"
        :prepend-icon="''"
        hide-details="auto"
        :rules="[maxFileSize(maxContractFileSizeMB)]"
        :disabled="loading"
        @update:model-value="handleFileUpdate($event)"
      ></v-file-input>

      <v-btn
        v-if="account.contractType === 'custom'"
        variant="outlined"
        class="mt-3"
        color="error"
        append-icon="mdi-delete"
        :loading="deletingContract"
        :disabled="deletingContract"
        @click="$emit('deleteContractCustom')"
        >{{ t("account.settings.contract.deleteCustomContract") }}
      </v-btn>
    </AdminOnlyCard>
  </div>
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import { areSupervisorOrgContractInfoValid } from "@/composables/useContract";
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import type { Account } from "@aircap/common/account";
import type { ContractTemplate, SupervisorOrg } from "@aircap/common/supervisorOrg";
import { computed } from "vue";

const props = defineProps<{
  account: Account;
  supervisorOrg: SupervisorOrg | null | undefined;
  loading: boolean;
  contractType: "signatureApi" | "custom" | null;
  contractTemplateId: string | null | undefined;
  contractCustomFile: File | null;
  signatureAPIStatuses: { operator: string; supervisorOrg: string };
  maxContractFileSizeMB: number;
  deletingContract: boolean;
  contractTemplates: (ContractTemplate & { id: string })[];
}>();

const emit = defineEmits<{
  "update:contractType": [value: "signatureApi" | "custom" | null];
  "update:contractTemplateId": [value: string | null | undefined];
  "update:contractCustomFile": [value: File | null];
  deleteContractCustom: [];
  downloadContract: [];
}>();

const { t } = useI18n();
const contractTemplateItems = computed(() =>
  props.contractTemplates.map((template) => ({ title: template.name, value: template.id })),
);
const contractInfoValid = computed(() =>
  areSupervisorOrgContractInfoValid(props.supervisorOrg, props.contractTemplates),
);
const { maxFileSize, required } = useRules();

const handleFileUpdate = (value: File | File[] | null) => {
  if (Array.isArray(value)) {
    emit("update:contractCustomFile", value[0] ?? null);
  } else {
    emit("update:contractCustomFile", value);
  }
};
</script>
